import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import { getApiBaseUrl } from '../utils/apiConfig';
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import {
  slidesAPI,
  videoPreviewAPI,
  filesystemAPI,
  conversationAPI,
} from "../services/api";
import slidePathMapping from "../services/slideMapping";
import { useConversationStore } from "../stores/conversationStore";
import { useUserStore } from "../stores/userStore";
import { toast } from "../hooks/use-toast";
import { slideLoadingOptimizer } from "../services/slideLoadingOptimizer";
import { performanceMonitor } from "../utils/performanceMonitor";
import { conversationUpdatesService } from "../services/conversationUpdatesService";
import { UnifiedSlideCard } from "./slides/UnifiedSlideCard";
import { detectSlideFormat, parseGsapSlide } from "../utils/slideTypeDetector";
import { useAgentJob } from "../hooks/useAgentJob";
import api from "../services/api";
import { DEFAULT_SLIDE_RESOLUTION } from "../config/gsapViewer";

// Add CSS animation styles
const animationStyles = `
  @keyframes fadeInUp {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }
  
  @keyframes slideUpdate {
    0% {
      transform: scale(1);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  }
  
  .slide-updated {
    animation: slideUpdate 0.6s ease-in-out;
    border-color: rgb(34 197 94) !important;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = animationStyles;
  if (!document.head.querySelector("[data-slide-gallery-styles]")) {
    styleElement.setAttribute("data-slide-gallery-styles", "true");
    document.head.appendChild(styleElement);
  }
}

interface Slide {
  id: string;
  number: string;
  title: string;
  path: string;
  content: string;
  source: 'plan' | 'html';
  planData?: {
    scene_id: string;
    title: string;
    layout_type: string;
    content_description: string;
    facts_used: string[];
    message: string;
    component_mapping: Record<string, string>;
    script_text: string;
    sync_points: string[];
    estimated_duration: number;
  };
  hasHtml: boolean;
  justUpdated?: boolean; // Flag for animation when slide is updated
}

interface LayoutState {
  id: string;
  referenceImagePath: string;
  generatedHtmlPath: string;
  title: string;
  description?: string;
  createdAt: string;
  tags?: string[];
}

interface SlideInfo {
  scene_id: string;
  title: string;
  layout_type: string;
  content_description?: string;
  facts_used?: string[];
  message?: string;
  component_mapping?: Record<string, string>;
  script_text?: string;
  sync_points?: string[];
  visual_instructions?: string;
  estimated_duration?: number;
  image_urls?: Array<{
    image_id: string;
    image_url: string;
  }>;
  start_time?: number;
  end_time?: number;
  generation_metadata?: {
    last_modified: string;
    timing_updated?: string;
  };
}

interface SlideGalleryProps {
  conversationId?: string;
  className?: string;
  onSlideClick?: (slidePath: string) => void;
  onVideoPreviewClick?: () => void;
  cachedSlides?: Slide[];
  cachedConversation?: any;
  navigatingToSlide?: string | null;
  highlightPending?: boolean; // Highlight slides that need generation
}

// Slide generation status tracking
interface SlideGenerationStatus {
  status: 'pending' | 'generating' | 'completed' | 'error';
  progress?: number; // 0-100
  jobId?: string;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
}

/**
 * LocalStorage key for persisting slide generation status
 * Scoped per conversation to avoid conflicts
 */
const getStorageKey = (conversationId: string) =>
  `slideGeneration_${conversationId}`;

/**
 * Save slideGenerationMap to localStorage
 * Only saves active (generating) jobs, not completed/error states
 */
const saveGenerationMapToStorage = (
  conversationId: string,
  map: Map<string, SlideGenerationStatus>
) => {
  try {
    // Only persist generating jobs (not completed or errored)
    const activeJobs: Record<string, SlideGenerationStatus> = {};

    map.forEach((status, sceneId) => {
      if (status.status === 'generating' && status.jobId) {
        activeJobs[sceneId] = {
          ...status,
          startedAt: status.startedAt instanceof Date ? status.startedAt.toISOString() as any : status.startedAt,
        };
      }
    });

    if (Object.keys(activeJobs).length > 0) {
      localStorage.setItem(getStorageKey(conversationId), JSON.stringify(activeJobs));
      console.log(`💾 Saved ${Object.keys(activeJobs).length} active jobs to localStorage`);
    } else {
      // Clear storage if no active jobs
      localStorage.removeItem(getStorageKey(conversationId));
    }
  } catch (error) {
    console.error('Failed to save generation map to localStorage:', error);
  }
};

/**
 * Load slideGenerationMap from localStorage
 * Restores active jobs on page refresh
 * Includes TTL validation to reject stale jobs
 */
const loadGenerationMapFromStorage = (
  conversationId: string
): Map<string, SlideGenerationStatus> => {
  try {
    const stored = localStorage.getItem(getStorageKey(conversationId));
    if (!stored) {
      return new Map();
    }

    const parsed = JSON.parse(stored) as Record<string, SlideGenerationStatus>;
    const map = new Map<string, SlideGenerationStatus>();
    const TTL = 10 * 60 * 1000; // 10 minutes max age for jobs
    const now = Date.now();
    let rejectedCount = 0;

    Object.entries(parsed).forEach(([sceneId, status]) => {
      // Convert ISO string back to Date
      const startedAt = status.startedAt ? new Date(status.startedAt as any) : undefined;

      // Check if job is too old (TTL expired)
      if (startedAt) {
        const age = now - startedAt.getTime();
        if (age > TTL) {
          console.warn(`⏱️ Rejecting stale job for ${sceneId}: age=${Math.round(age/1000)}s (max ${TTL/1000}s)`);
          rejectedCount++;
          return; // Skip this job
        }
      }

      // Only restore generating jobs (not error/completed)
      if (status.status === 'generating') {
        map.set(sceneId, {
          ...status,
          startedAt,
        });
      }
    });

    if (rejectedCount > 0) {
      console.log(`💾 Loaded ${map.size} active jobs from localStorage (rejected ${rejectedCount} stale jobs)`);
      // Clear rejected jobs from storage
      setTimeout(() => {
        saveGenerationMapToStorage(conversationId, map);
      }, 100);
    } else {
      console.log(`💾 Loaded ${map.size} active jobs from localStorage`);
    }

    return map;
  } catch (error) {
    console.error('Failed to load generation map from localStorage:', error);
    return new Map();
  }
};

// Memoize component to prevent unnecessary re-renders when navigating slides
const SlideGalleryComponent: React.FC<SlideGalleryProps> = ({
  conversationId,
  className = "",
  onSlideClick,
  onVideoPreviewClick,
  cachedSlides,
  cachedConversation,
  navigatingToSlide,
  highlightPending: highlightPendingProp,
}) => {
  const [slides, setSlides] = useState<Slide[]>(cachedSlides || []);
  const [slideInfoMap, setSlideInfoMap] = useState<Map<string, SlideInfo>>(
    new Map(),
  );
  const [layoutState, setLayoutState] = useState<LayoutState[]>([]);
  const [loading, setLoading] = useState(!cachedSlides);
  const [error, setError] = useState<string | null>(null);
  const [hasIndexFiles, setHasIndexFiles] = useState(false);
  const [checkingIndexFiles, setCheckingIndexFiles] = useState(false);
  // Removed auto-selection - users will manually click slides when needed
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  // Removed export dialog state - feature is waitlisted

  // Slide generation tracking - Map<scene_id, SlideGenerationStatus>
  // Initialize from localStorage to survive page refreshes
  const [slideGenerationMap, setSlideGenerationMap] = useState<Map<string, SlideGenerationStatus>>(() => {
    if (conversationId) {
      return loadGenerationMapFromStorage(conversationId);
    }
    return new Map();
  });

  // Check URL params for highlight=pending and highlightSlide
  const urlParams = new URLSearchParams(window.location.search);
  const highlightPending = highlightPendingProp || urlParams.get('highlight') === 'pending';
  const highlightSlideNumber = urlParams.get('highlightSlide') ? parseInt(urlParams.get('highlightSlide')!, 10) : null;

  // Ref for highlighted slide element (for auto-scroll)
  const highlightedSlideRef = useRef<HTMLDivElement | null>(null);

  // Get conversation data from store
  const { conversations } = useConversationStore();
  const activeTeamId = useUserStore((s) => s.activeTeam?.id);
  const currentConversation = conversations.find(
    (c) => c.id === conversationId,
  );

  // Use conversation from store if available, otherwise use fetched details
  const effectiveConversation = currentConversation || conversationDetails;
  const isStyleConversation =
    effectiveConversation?.collectionType === "STYLE";

  // Fetch conversation details if not available in store
  useEffect(() => {
    // Use cached conversation if available
    if (cachedConversation) {
      setConversationDetails(cachedConversation);
      console.log("SlideGallery - Using cached conversation details");
      return;
    }
    
    const fetchConversationDetails = async () => {
      if (conversationId && !currentConversation && !conversationDetails) {
        try {
          const response = await conversationAPI.getById(conversationId);
          setConversationDetails(response.conversation);
          console.log(
            "SlideGallery - Fetched conversation details:",
            response.conversation,
          );
        } catch (error) {
          console.error(
            "SlideGallery - Failed to fetch conversation details:",
            error,
          );
        }
      }
    };

    fetchConversationDetails();
  }, [conversationId, currentConversation, conversationDetails, cachedConversation]);

  // Debug logging
  useEffect(() => {
    if (conversationId) {
      console.log("SlideGallery - Conversation ID:", conversationId);
      console.log("SlideGallery - Current conversation:", currentConversation);
      console.log("SlideGallery - Conversation details:", conversationDetails);
      console.log(
        "SlideGallery - Effective conversation:",
        effectiveConversation,
      );
      console.log("SlideGallery - Is style conversation:", isStyleConversation);
      console.log("SlideGallery - All conversations:", conversations);
    }
  }, [
    conversationId,
    currentConversation,
    conversationDetails,
    effectiveConversation,
    isStyleConversation,
    conversations,
  ]);

  // Auto-save slideGenerationMap to localStorage on changes
  useEffect(() => {
    if (conversationId) {
      saveGenerationMapToStorage(conversationId, slideGenerationMap);
    }
  }, [conversationId, slideGenerationMap]);

  // Scroll to highlighted slide when specified in URL
  useEffect(() => {
    if (highlightSlideNumber !== null && highlightedSlideRef.current) {
      // Wait for animations to complete, then scroll
      const timer = setTimeout(() => {
        highlightedSlideRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        console.log(`🎯 Scrolled to slide ${highlightSlideNumber}`);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [highlightSlideNumber, slides]);

  // Auto-selection removed - search params no longer needed for this

  // Function to load state.json for style conversations
  const loadLayoutState = async (): Promise<LayoutState[]> => {
    try {
      console.log(`SlideGallery - Loading state.json for conversation: ${conversationId}`);
      const stateResponse = await filesystemAPI.read("state.json", conversationId);
      const stateData = JSON.parse(stateResponse.content);
      
      if (stateData.layouts && Array.isArray(stateData.layouts)) {
        console.log(`SlideGallery - Found ${stateData.layouts.length} layouts in state.json`);
        return stateData.layouts;
      }
      
      console.log("SlideGallery - No layouts found in state.json");
      return [];
    } catch (error) {
      console.warn("SlideGallery - Could not load state.json:", error);
      return [];
    }
  };

  // Function to fetch layout files for style conversations
  const fetchLayoutFiles = async (): Promise<Slide[]> => {
    try {
      // For style conversations, we need to find layout_*.html files
      // Try multiple locations: layouts/, root directory, and style/ directory
      const locationsToTry = ["layouts", ".", "style"];
      let allLayoutFiles: any[] = [];

      interface FileItem {
        name: string;
        path: string;
        type: "file" | "directory";
      }

      for (const location of locationsToTry) {
        try {
          console.log(
            `SlideGallery - Trying to browse location: ${location} for conversation: ${conversationId}`,
          );
          const dirResponse = await filesystemAPI.browse(
            location,
            conversationId,
          );

          if (dirResponse.files) {
            const layoutFiles = dirResponse.files
              .filter(
                (file: FileItem) =>
                  file.type === "file" &&
                  file.name.startsWith("layout_") &&
                  file.name.endsWith(".html"),
              )
              .map((file: FileItem) => ({ ...file, location }));

            console.log(
              `SlideGallery - Found ${layoutFiles.length} layout files in ${location}:`,
              layoutFiles,
            );
            allLayoutFiles = [...allLayoutFiles, ...layoutFiles];
          }
        } catch (error) {
          console.warn(`SlideGallery - Could not browse ${location}:`, error);
        }
      }

      // Remove duplicates based on file name and convert to Slide format
      const uniqueLayoutFiles = allLayoutFiles.reduce(
        (unique: FileItem[], file: FileItem) => {
          if (!unique.find((existing) => existing.name === file.name)) {
            unique.push(file);
          }
          return unique;
        },
        [],
      );

      console.log(
        `SlideGallery - Unique layout files found:`,
        uniqueLayoutFiles,
      );

      // Read the actual HTML content for each layout file
      const slides = await Promise.all(
        uniqueLayoutFiles.map(async (file: FileItem, index: number) => {
          let htmlContent = `Layout: ${file.name}`;

          try {
            console.log(`SlideGallery - Reading content for: ${file.path}`);
            const fileResponse = await filesystemAPI.read(
              file.path,
              conversationId,
            );
            htmlContent = fileResponse.content;
            console.log(
              `SlideGallery - Successfully read content for ${file.name}, length: ${htmlContent.length}`,
            );
          } catch (error) {
            console.warn(
              `SlideGallery - Could not read content for ${file.path}:`,
              error,
            );
          }

          return {
            id: `layout-${index}`,
            number: (file.name || "")
              .replace("layout_", "")
              .replace(".html", ""),
            title: (file.name || "")
              .replace("layout_", "")
              .replace(".html", "")
              .replace(/_/g, " "),
            path: file.path,
            content: htmlContent,
          };
        }),
      );

      console.log(`SlideGallery - Converted to Scenes:`, slides);
      return slides;
    } catch (error) {
      console.error("Failed to fetch layout files:", error);
      return [];
    }
  };

  useEffect(() => {
    // Skip fetching if we have cached slides
    if (cachedSlides && cachedSlides.length > 0) {
      console.log('SlideGallery - Using cached slides, processing GSAP slides...');

      // Process cached GSAP slides to extract metadata
      const processCachedSlides = async () => {
        const processed = await Promise.all(
          cachedSlides.map(async (slide) => {
            const slideFormat = detectSlideFormat(slide.path);

            if (slideFormat === 'gsap') {
              try {
                const gsapContent = slide.content;
                console.log(`📦 SlideGallery - Processing cached GSAP slide ${slide.number}:`, {
                  slidePath: slide.path,
                  hasContent: !!gsapContent,
                  contentType: typeof gsapContent,
                  contentLength: gsapContent?.length,
                });
                const gsapSlide = await parseGsapSlide(slide.path, gsapContent, slide.number);
                return gsapSlide as any;
              } catch (error) {
                console.error(`Failed to process cached GSAP slide ${slide.path}:`, error);
                return slide;
              }
            }
            return slide;
          })
        );

        setSlides(processed);
        setLoading(false);

        // Still fetch slide info for HTML slides
        if (conversationId && !isStyleConversation) {
          fetchAllSlideInfo(processed);
        }
      };

      processCachedSlides();
      return;
    }
    
    const fetchSlides = async () => {
      if (!conversationId) {
        setSlides([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        let slidesData: Slide[] = [];

        if (isStyleConversation) {
          // For style conversations, fetch layout files and state
          slidesData = await fetchLayoutFiles();
          const layoutStateData = await loadLayoutState();
          setLayoutState(layoutStateData);
        } else {
          // For video conversations, fetch slides as usual
          const data = await slidesAPI.getByConversation(conversationId);
          slidesData = data.slides || [];

          // Process GSAP slides - detect format and fetch JSON data for GSAP slides
          const processedSlides = await Promise.all(
            slidesData.map(async (slide) => {
              const slideFormat = detectSlideFormat(slide.path);

              if (slideFormat === 'gsap') {
                // Parse GSAP slide JSON from content (already merged with info.json by backend)
                try {
                  // Use slide.content which contains merged slide.json + info.json data
                  const gsapContent = slide.content; // Backend already merged info.json

                  // Debug: Check what data we received from backend
                  console.log(`📦 SlideGallery - Processing GSAP slide ${slide.number}:`, {
                    slidePath: slide.path,
                    hasContent: !!gsapContent,
                    contentType: typeof gsapContent,
                    contentLength: gsapContent?.length,
                    contentPreview: gsapContent?.substring(0, 200),
                    slideKeys: Object.keys(slide)
                  });

                  const gsapSlide = await parseGsapSlide(slide.path, gsapContent, slide.number);

                  // Return as UnifiedSlide with GSAP format
                  return gsapSlide as any; // Cast to Slide type for now
                } catch (error) {
                  console.error(`Failed to load GSAP slide ${slide.path}:`, error);
                  // Return original slide if GSAP parsing fails
                  return slide;
                }
              }

              // Return HTML slide as-is
              return slide;
            })
          );

          slidesData = processedSlides;

          // Debug: Log processed slides structure
          console.log('📊 SlideGallery - Processed slides:', slidesData.map(s => ({
            number: s.number,
            id: s.id,
            hasFormat: 'format' in s,
            format: (s as any).format,
            hasSource: 'source' in s,
            source: s.source,
            hasContent: !!s.content,
            hasGsapData: !!(s as any).gsapData,
            pathEnding: s.path?.split('/').pop(),
          })));
        }

        setSlides(slidesData);

        // For video conversations, fetch slide info and handle mappings
        if (slidesData.length > 0 && !isStyleConversation) {
          performanceMonitor.startTiming('SlideGallery.fetchAllSlideInfo', { slideCount: slidesData.length });
          fetchAllSlideInfo(slidesData).then(() => {
            const duration = performanceMonitor.endTiming('SlideGallery.fetchAllSlideInfo');
            console.log(`SlideGallery - Loaded slide info in ${duration?.toFixed(2)}ms`);
          });

          // Populate slide path mappings (only for HTML slides)
          const htmlSlidePaths = slidesData
            .filter(slide => slide.source === 'html' && slide.path)
            .map((slide) => slide.path);
          await slidePathMapping.populateMappings(htmlSlidePaths);
        }

        // Auto-selection removed - users will click slides when they want to view them
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, [
    conversationId,
    onSlideClick,
    isStyleConversation,
    effectiveConversation,
    cachedSlides,
  ]);

  // Listen for slide updates via SSE
  useEffect(() => {
    if (!conversationId || isStyleConversation) {
      return;
    }

    // Prefer the conversation's own team, fall back to the user's active team.
    const teamId = effectiveConversation?.teamId || activeTeamId;
    if (!teamId) {
      return;
    }

    // Start listening for updates
    conversationUpdatesService.startListening(teamId);
    
    // Subscribe to conversation updates
    const unsubscribe = conversationUpdatesService.onConversationUpdate(
      async (convId, updates) => {
        if (convId === conversationId && updates.slidesUpdated) {
          console.log('SlideGallery - Slide update detected:', updates);
          
          if (updates.isContentUpdate && updates.slideNumber) {
            // Content update - refresh just that slide
            try {
              // First, clear the cache for the updated slide
              const currentSlide = slides.find(s => parseInt(s.number) === updates.slideNumber);
              if (currentSlide && currentSlide.path) {
                slideLoadingOptimizer.clearSlideCache(currentSlide.path, conversationId);
                console.log(`SlideGallery - Cleared cache for slide ${updates.slideNumber}`);
              }
              
              // Fetch updated slide data with cache-busting
              const data = await slidesAPI.getByConversation(conversationId, true); // Force fresh data
              const updatedSlides = data.slides || [];
              
              console.log(`SlideGallery - Looking for slide ${updates.slideNumber} in ${updatedSlides.length} slides`);
              console.log(`SlideGallery - Slide numbers:`, updatedSlides.map(s => `${s.number} (parsed: ${parseInt(s.number)})`));
              
              // Find the updated slide
              const updatedSlide = updatedSlides.find(
                s => parseInt(s.number) === updates.slideNumber
              );
              
              if (updatedSlide) {
                // Debug: Log the content we received
                console.log(`SlideGallery - Found updated slide ${updates.slideNumber}`);
                console.log(`SlideGallery - Updated slide content length:`, updatedSlide.content?.length);
                console.log(`SlideGallery - First 200 chars of new content:`, updatedSlide.content?.substring(0, 200));
                
                // Check if content actually changed
                const oldSlide = slides.find(s => parseInt(s.number) === updates.slideNumber);
                const contentChanged = oldSlide?.content !== updatedSlide.content;
                console.log(`SlideGallery - Content actually changed: ${contentChanged}`);
                
                if (contentChanged) {
                  // Force complete re-render by creating new slide object with new ID
                  const updatedSlideWithNewId = {
                    ...updatedSlide,
                    id: `${updatedSlide.id}_updated_${Date.now()}`, // Force new ID for React to detect change
                    justUpdated: true,
                    planData: updatedSlide.planData || oldSlide?.planData, // Preserve planData for scene_id tracking
                  };
                  
                  // Update the specific slide in state
                  setSlides(prevSlides => 
                    prevSlides.map(slide => 
                      parseInt(slide.number) === updates.slideNumber 
                        ? updatedSlideWithNewId
                        : slide
                    )
                  );
                  
                  // Clear the update flag after animation
                  setTimeout(() => {
                    setSlides(prevSlides => 
                      prevSlides.map(slide => 
                        parseInt(slide.number) === updates.slideNumber 
                          ? { ...slide, justUpdated: false }
                          : slide
                      )
                    );
                  }, 600);
                  
                  // Update slide info if it's an HTML slide - clear cache and force reload
                  if (updatedSlide.source === 'html' && updatedSlide.path) {
                    // Clear the cache for this specific slide first
                    slideLoadingOptimizer.clearSlideCache(updatedSlide.path, conversationId);

                    // Now load fresh slide info without cache
                    const slideInfo = await slideLoadingOptimizer.loadSlideInfo(
                      updatedSlide.path,
                      conversationId,
                      { useCache: false } // Force fresh load
                    );
                    if (slideInfo) {
                      setSlideInfoMap(prev => new Map(prev).set(updatedSlide.path, slideInfo));
                    }
                  }

                  // Re-check for active jobs after slide update
                  // This ensures generation progress persists across slide refreshes
                  console.log('🔄 Slide updated - re-checking active jobs to restore progress');
                  recoverActiveJobs();
                } else {
                  console.log('SlideGallery - Content unchanged, skipping update');
                }
              } else {
                console.warn(`SlideGallery - Could not find slide ${updates.slideNumber} in fetched slides`);
              }
            } catch (error) {
              console.error('Failed to update slide:', error);
            }
            
          } else if (updates.isNewSlide) {
            // New slide added - fetch all slides to get the complete list
            try {
              const data = await slidesAPI.getByConversation(conversationId, true); // Force fresh data for new slides
              const newSlides = data.slides || [];
              
              // Smart merge: keep existing, add new ones
              setSlides(prevSlides => {
                const existingNumbers = new Set(prevSlides.map(s => s.number));
                const slidesToAdd = newSlides.filter(s => !existingNumbers.has(s.number));
                
                // Add animation flag to new slides
                const animatedNewSlides = slidesToAdd.map(s => ({ ...s, justUpdated: true }));
                
                // Clear animation flag after delay
                setTimeout(() => {
                  setSlides(prev => 
                    prev.map(slide => 
                      slidesToAdd.some(ns => ns.number === slide.number)
                        ? { ...slide, justUpdated: false }
                        : slide
                    )
                  );
                }, 600);
                
                return [...prevSlides, ...animatedNewSlides].sort((a, b) => {
                  const numA = parseInt(a.number) || 0;
                  const numB = parseInt(b.number) || 0;
                  return numA - numB;
                });
              });
              
              // Fetch info for new slides
              const newSlidesToProcess = newSlides.filter(
                s => !slides.some(existing => existing.number === s.number)
              );
              if (newSlidesToProcess.length > 0) {
                fetchAllSlideInfo(newSlidesToProcess);
              }
            } catch (error) {
              console.error('Failed to fetch new slides:', error);
            }
          }
        }
      }
    );
    
    // Cleanup
    return () => {
      unsubscribe();
    };
  }, [conversationId, isStyleConversation, effectiveConversation?.teamId, activeTeamId]);

  const fetchAllSlideInfo = async (slidesData: Slide[]) => {
    const infoMap = new Map<string, SlideInfo>();

    // Check if lazy loading is enabled (default: true for performance)
    const useLazyLoading = localStorage.getItem('slideGallery.lazyLoading') !== 'false';
    
    console.log('SlideGallery - Lazy loading enabled:', useLazyLoading);
    console.log('SlideGallery - Total slides to load:', slidesData.length);

    if (useLazyLoading && slidesData.length > 3) {
      // Only load first 3 slides initially for performance
      console.log('SlideGallery - Using lazy loading, loading first 3 slides only');
      const initialSlides = slidesData.slice(0, 3);
      
      const slideInfos = await slideLoadingOptimizer.loadSlidesBatch(
        initialSlides,
        conversationId,
        { batchSize: 3, useCache: true }
      );
      
      // Map the initial results
      initialSlides.forEach((slide, index) => {
        if (slide.source === 'html' && slide.path && slideInfos[index]) {
          infoMap.set(slide.path, slideInfos[index]);
        }
      });
      
      // Load remaining slides in background
      if (slidesData.length > 3) {
        setTimeout(async () => {
          console.log('SlideGallery - Loading remaining slides in background');
          const remainingSlides = slidesData.slice(3);
          const remainingInfos = await slideLoadingOptimizer.loadSlidesBatch(
            remainingSlides,
            conversationId,
            { batchSize: 2, useCache: true }
          );
          
          remainingSlides.forEach((slide, index) => {
            if (slide.source === 'html' && slide.path && remainingInfos[index]) {
              infoMap.set(slide.path, remainingInfos[index]);
              // Trigger re-render if needed
              setSlideInfoMap(new Map(infoMap));
            }
          });
        }, 100);
      }
    } else {
      // Fallback to original behavior for backward compatibility
      const htmlSlides = slidesData.filter(slide => slide.source === 'html' && slide.path);

      // Fetch all info.json files in parallel
      const infoPromises = htmlSlides.map(async (slide) => {
        try {
          const slideDir = slide.path.substring(0, slide.path.lastIndexOf("/"));
          const infoPath = `${slideDir}/info.json`;
          const response = await filesystemAPI.read(infoPath);
          const info = JSON.parse(response.content);
          return { path: slide.path, info };
        } catch (err) {
          console.warn(`Could not load info for ${slide.path}:`, err);
          return null;
        }
      });

      const results = await Promise.all(infoPromises);
      results.forEach((result) => {
        if (result) {
          infoMap.set(result.path, result.info);
        }
      });
    }

    setSlideInfoMap(infoMap);
  };

  const handleExportClick = () => {
    // Show waitlist notification
    toast({
      title: "Feature Coming Soon",
      description: "You're on the waitlist for the export feature. We'll notify you when it's available!",
      duration: 5000,
    });
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  /**
   * Recover active slide generation jobs
   * Can be called on mount, conversation change, or after slide updates
   */
  const recoverActiveJobs = useCallback(async () => {
    if (!conversationId) return;

    try {
      console.log('🔄 Checking for active slide generation jobs...');

      // Fetch all active agent jobs for this conversation
      const response = await api.get(`/conversations/${conversationId}/agent-jobs/active`);

      console.log('🔄 Recovery response:', {
        success: response.data.success,
        totalJobs: response.data.jobs?.length,
        jobs: response.data.jobs?.map((j: any) => ({
          id: j.id,
          agentName: j.agentName,
          status: j.status,
          slideId: j.metadata?.slideId,
        })),
      });

      if (response.data.success && response.data.jobs) {
        // Filter for slide_builder jobs with status pending or active
        const slideBuilderJobs = response.data.jobs.filter(
          (job: any) => job.agentName === 'slide_builder' && ['pending', 'active'].includes(job.status)
        );

        console.log(`🔄 Found ${slideBuilderJobs.length} slide_builder jobs to recover`);

        if (slideBuilderJobs.length > 0) {
          // Merge recovered jobs with existing map (don't replace)
          setSlideGenerationMap(prevMap => {
            const mergedMap = new Map(prevMap);

            for (const job of slideBuilderJobs) {
              const slideId = job.metadata?.slideId;
              if (slideId) {
                // Only recover if not already in map or if it's stale
                const existing = prevMap.get(slideId);
                if (!existing || existing.status !== 'generating') {
                  console.log(`🔄 Recovering job for slide ${slideId}:`, {
                    jobId: job.id,
                    status: job.status,
                    progress: job.progress?.percentage,
                  });

                  mergedMap.set(slideId, {
                    status: 'generating',
                    progress: job.progress?.percentage || 0,
                    jobId: job.id,
                    startedAt: new Date(job.createdAt),
                  });

                  // Resume polling for this job
                  pollSlideGenerationJob(slideId, job.id).catch(error => {
                    console.error(`❌ Failed to resume polling for ${slideId}:`, error);
                  });
                } else {
                  console.log(`⏭️  Skipping recovery for ${slideId} - already generating`);
                }
              } else {
                console.warn(`⚠️ Job ${job.id} has no slideId in metadata`);
              }
            }

            console.log(`✅ Recovery complete - merged ${slideBuilderJobs.length} jobs (total: ${mergedMap.size})`);
            return mergedMap;
          });
        }
      }
    } catch (error) {
      console.error('❌ Failed to recover active jobs:', error);
    }
  }, [conversationId]);

  /**
   * Recover active jobs on mount or conversation change
   */
  useEffect(() => {
    recoverActiveJobs();
  }, [conversationId, recoverActiveJobs]);

  /**
   * Handle editing a slide - dispatches custom event to populate chat input
   */
  const handleEditSlide = (slideId: string, sceneNumber: number) => {
    console.log(`✏️ Edit requested for slide ${slideId} (Scene ${sceneNumber})`);

    // Dispatch custom event to MessageInput
    const event = new CustomEvent('slideEditRequest', {
      detail: { slideId, sceneNumber }
    });
    window.dispatchEvent(event);

    // Log for debugging
    console.log('📤 Dispatched slideEditRequest event:', { slideId, sceneNumber });
  };

  /**
   * Handle clearing a stuck/error generation status
   * Removes the error from slideGenerationMap and localStorage
   */
  const handleClearGenerationError = (slideId: string) => {
    console.log(`🧹 Clearing generation error for slide ${slideId}`);

    // Remove from map
    setSlideGenerationMap(prev => {
      const newMap = new Map(prev);
      newMap.delete(slideId);
      return newMap;
    });

    // Clear from localStorage
    if (conversationId) {
      setTimeout(() => {
        setSlideGenerationMap(prev => {
          saveGenerationMapToStorage(conversationId, prev);
          return prev;
        });
      }, 100);
    }

    toast({
      title: "Status Cleared",
      description: "Generation status has been cleared",
    });
  };

  /**
   * Handle cancelling an active slide generation
   * Calls backend API to cancel MongoDB job and clears client state
   */
  const handleCancelSlideGeneration = async (slideId: string) => {
    if (!conversationId) return;

    console.log(`🛑 Cancelling generation for slide ${slideId}`);

    try {
      // Call API to cancel the MongoDB job
      const response = await api.post(
        `/conversations/${conversationId}/slides/${slideId}/cancel`
      );

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to cancel generation');
      }

      console.log(`✅ Successfully cancelled generation for slide ${slideId}`);

      // Clear from slideGenerationMap
      setSlideGenerationMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(slideId);
        return newMap;
      });

      // Clear from localStorage
      setTimeout(() => {
        setSlideGenerationMap(prev => {
          saveGenerationMapToStorage(conversationId, prev);
          return prev;
        });
      }, 100);

      toast({
        title: "Generation Cancelled",
        description: "Slide generation has been cancelled",
      });
    } catch (error) {
      console.error(`❌ Failed to cancel generation for slide ${slideId}:`, error);

      // Still try to clear client state even if API call failed
      setSlideGenerationMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(slideId);
        return newMap;
      });

      toast({
        title: "Cancellation Error",
        description: error instanceof Error ? error.message : 'Failed to cancel generation, but cleared local state',
        variant: "destructive",
      });
    }
  };

  /**
   * Poll agent job until completion
   */
  const pollSlideGenerationJob = async (slideId: string, jobId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const MAX_POLL_TIME = 5 * 60 * 1000; // 5 minutes timeout

      const interval = setInterval(async () => {
        try {
          // Check if polling has exceeded max time
          const elapsed = Date.now() - startTime;
          if (elapsed > MAX_POLL_TIME) {
            clearInterval(interval);
            console.error(`⏱️ Polling timeout for slide ${slideId} after ${elapsed}ms`);

            setSlideGenerationMap(prev => {
              const newMap = new Map(prev);
              newMap.set(slideId, {
                status: 'error',
                error: 'Generation timeout (>5min). The job may have stalled or the server restarted.',
                jobId,
              });
              return newMap;
            });

            // Clear from localStorage
            if (conversationId) {
              saveGenerationMapToStorage(conversationId, new Map());
            }

            reject(new Error('Polling timeout'));
            return;
          }

          const response = await api.get(`/agent-jobs/${jobId}`);
          const job = response.data.job;

          // Update progress
          if (job.progress?.percentage != null) {
            setSlideGenerationMap(prev => new Map(prev).set(slideId, {
              status: 'generating',
              progress: job.progress.percentage,
              jobId,
              startedAt: new Date(job.createdAt),
            }));
          }

          // Check terminal status
          if (job.status === 'completed') {
            clearInterval(interval);

            setSlideGenerationMap(prev => {
              const newMap = new Map(prev);
              newMap.delete(slideId); // Remove completed job from map
              return newMap;
            });

            // Clear completed job from localStorage
            if (conversationId) {
              setTimeout(() => {
                setSlideGenerationMap(prev => {
                  saveGenerationMapToStorage(conversationId, prev);
                  return prev;
                });
              }, 100);
            }

            // Refresh slides to get the newly generated slide
            if (!conversationId) {
              console.warn('Cannot refresh slides: conversationId is undefined');
              resolve();
              return;
            }

            console.log(`✅ Slide ${slideId} generation completed, refreshing slides...`);
            const data = await slidesAPI.getByConversation(conversationId, true);
            const updatedSlides = data.slides || [];

            // Process GSAP slides
            const processedSlides = await Promise.all(
              updatedSlides.map(async (slide) => {
                const slideFormat = detectSlideFormat(slide.path);
                if (slideFormat === 'gsap') {
                  try {
                    // Use slide.content which contains merged slide.json + info.json data
                    const gsapContent = slide.content; // Backend already merged info.json

                    // Debug: Check what data we received from backend after generation
                    console.log(`📦 SlideGallery - Refreshing GSAP slide ${slide.number}:`, {
                      slidePath: slide.path,
                      hasContent: !!gsapContent,
                      contentType: typeof gsapContent,
                      contentLength: gsapContent?.length,
                      contentPreview: gsapContent?.substring(0, 200),
                      slideKeys: Object.keys(slide)
                    });

                    const gsapSlide = await parseGsapSlide(slide.path, gsapContent, slide.number);
                    return gsapSlide as any;
                  } catch (error) {
                    console.error(`Failed to load GSAP slide ${slide.path}:`, error);
                    return slide;
                  }
                }
                return slide;
              })
            );

            setSlides(processedSlides);

            // Fetch slide info for new slides
            if (processedSlides.length > 0) {
              fetchAllSlideInfo(processedSlides);
            }

            toast({
              title: "Slide Generated",
              description: `Slide ${slideId} has been generated successfully!`,
            });

            resolve(job.result);
          } else if (job.status === 'failed') {
            clearInterval(interval);

            setSlideGenerationMap(prev => new Map(prev).set(slideId, {
              status: 'error',
              error: job.error || 'Generation failed',
              jobId,
            }));

            // Keep error in localStorage temporarily for user to see
            // Will be cleared manually or after TTL expires
            if (conversationId) {
              setTimeout(() => {
                setSlideGenerationMap(prev => {
                  saveGenerationMapToStorage(conversationId, prev);
                  return prev;
                });
              }, 100);
            }

            reject(new Error(job.error || 'Generation failed'));
          }
        } catch (error) {
          clearInterval(interval);

          setSlideGenerationMap(prev => new Map(prev).set(slideId, {
            status: 'error',
            error: error instanceof Error ? error.message : 'Polling failed',
          }));

          // Save error state to localStorage
          if (conversationId) {
            setTimeout(() => {
              setSlideGenerationMap(prev => {
                saveGenerationMapToStorage(conversationId, prev);
                return prev;
              });
            }, 100);
          }

          reject(error);
        }
      }, 1000); // Poll every second
    });
  };

  // Check for index.html files
  useEffect(() => {
    const checkIndexFiles = async () => {
      if (!conversationId) {
        setHasIndexFiles(false);
        return;
      }

      try {
        setCheckingIndexFiles(true);
        const data = await videoPreviewAPI.findIndexFiles(conversationId);
        setHasIndexFiles(data.count > 0);
      } catch (err) {
        console.warn("Failed to check for index files:", err);
        setHasIndexFiles(false);
      } finally {
        setCheckingIndexFiles(false);
      }
    };

    checkIndexFiles();
  }, [conversationId]);

  const extractSlideNumber = (path: string): string => {
    const match = path.match(/slide-?(\d+)/i);
    return match ? match[1] : "0";
  };


  const extractTitle = (content: string): string => {
    const titleMatch = content.match(/<title>(.*?)<\/title>/i);
    if (titleMatch) {
      return titleMatch[1].replace(/^Slide \d+:\s*/, "");
    }

    const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
    return h1Match ? h1Match[1] : "Untitled Slide";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">
          Loading {isStyleConversation ? "layouts" : "scenes"}...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-destructive">Error: {error}</div>
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">
          Please select a conversation to view{" "}
          {isStyleConversation ? "layouts" : "scenes"}
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">
          {loading
            ? `Loading ${isStyleConversation ? "layouts" : "scenes"}...`
            : `No ${isStyleConversation ? "layouts" : "scenes"} found in this conversation`}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`h-full flex flex-col bg-gradient-to-br from-background via-background to-muted/5 ${className} relative`}
    >
      {/* Loading overlay when navigating */}
      {navigatingToSlide && (
        <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading slide...</p>
          </div>
        </div>
      )}
      {/* Professional Header */}
      <div className="flex-shrink-0 bg-background/95 backdrop-blur-xl border-b border-border/30 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-xl flex items-center justify-center border border-primary/20 shadow-lg">
                  <svg
                    className="w-6 h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary/20 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="space-y-1">
                <h1 className="text-xl font-semibold text-foreground tracking-tight">
                  {isStyleConversation ? "Layout Gallery" : "Scenes Gallery"}
                </h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    {slides.length}{" "}
                    {isStyleConversation
                      ? slides.length === 1
                        ? "layout"
                        : "layouts"
                      : slides.length === 1
                        ? "scene"
                        : "scenes"}
                  </span>
                  <div className="w-1 h-1 bg-muted-foreground/40 rounded-full"></div>
                  <span>
                    {isStyleConversation
                      ? "Style templates"
                      : "Ready for preview"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Bar */}
            <div className="flex items-center gap-3">
              {/* Export Style Button - Only for style conversations */}
              {isStyleConversation && slides.length > 0 && (
                <Button
                  onClick={handleExportClick}
                  variant="default"
                  size="sm"
                  className="h-9 px-4 font-medium transition-all hover:shadow-md"
                  title="You're on the waitlist for this feature"
                >
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Export Style
                </Button>
              )}

              {/* Video Preview Button */}
              {onVideoPreviewClick && (
                <Button
                  onClick={onVideoPreviewClick}
                  disabled={!hasIndexFiles || checkingIndexFiles}
                  variant={hasIndexFiles ? "default" : "outline"}
                  size="sm"
                  className="h-9 px-4 font-medium transition-all hover:shadow-md"
                  title={
                    hasIndexFiles
                      ? "View full video preview"
                      : "Video preview requires index.html file"
                  }
                >
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15M9 10V9a2 2 0 012-2h2a2 2 0 012 2v1m-6 0V9a2 2 0 012-2h2a2 2 0 012 2v1"
                    />
                  </svg>
                  {checkingIndexFiles
                    ? "Checking..."
                    : hasIndexFiles
                      ? "Video Preview"
                      : "Generate Video"}
                </Button>
              )}

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 w-7 p-0"
                  title="Grid view"
                  onClick={() => setViewMode("grid")}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </Button>
                <Button
                  variant={viewMode === "table" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 w-7 p-0"
                  title="Table view"
                  onClick={() => setViewMode("table")}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Gallery Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-4">
          {/* Stats Bar */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-border/30">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium text-foreground">
                  {slides.length}
                </span>{" "}
                scenes
              </div>
              {slides.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>All scenes loaded</span>
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Click any slide to view • Ctrl+Click to open in new tab
            </div>
          </div>

          {/* Content based on view mode */}
          {viewMode === "grid" ? (
            /* Grid View */
            <div className={`grid gap-5 auto-rows-max ${
              isStyleConversation
                ? "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
            }`}>
              {slides.map((slide, index) => {
                const isPending = slide.source === 'plan' || !slide.hasHtml;
                const shouldHighlight = highlightPending && isPending;

                // Extract slide number for highlighting specific slide
                const slideNumber = slide.number || (index + 1);
                const isHighlightedSlide = highlightSlideNumber !== null && slideNumber === highlightSlideNumber;

                return (
                <div
                  key={slide.id}
                  ref={isHighlightedSlide ? highlightedSlideRef : null}
                  className={cn(
                    "group transform transition-all duration-500 hover:scale-[1.01] hover:-translate-y-1 fade-in-up",
                    slide.justUpdated && "slide-updated",
                    shouldHighlight && "ring-2 ring-orange-400 ring-offset-2 rounded-lg",
                    isHighlightedSlide && "ring-4 ring-primary ring-offset-4 rounded-lg shadow-2xl"
                  )}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    opacity: 0,
                  }}
                >
                  {isStyleConversation ? (
                    <StyleLayoutCard
                      slide={slide}
                      layoutState={layoutState.find(ls => 
                        ls.generatedHtmlPath === slide.path || 
                        ls.generatedHtmlPath.includes(slide.path.split('/').pop() || '')
                      )}
                      extractTitle={extractTitle}
                      onSlideClick={onSlideClick}
                      conversationId={conversationId}
                      index={index}
                    />
                  ) : slide.source === 'plan' ? (
                    <PlanOnlySlideCard
                      slide={slide}
                      extractSlideNumber={extractSlideNumber}
                      formatDuration={formatDuration}
                      onCancelGeneration={handleCancelSlideGeneration}
                      onClearError={handleClearGenerationError}
                      generationStatus={slideGenerationMap.get(slide.planData?.scene_id || '')}
                    />
                  ) : (
                    <UnifiedSlideCard
                      slide={slide}
                      slideInfo={slideInfoMap.get(slide.path)}
                      extractSlideNumber={extractSlideNumber}
                      extractTitle={extractTitle}
                      onSlideClick={onSlideClick}
                      onEditClick={handleEditSlide}
                      formatDuration={formatDuration}
                      index={index}
                      conversationId={conversationId}
                      generationStatus={slideGenerationMap.get(
                        slideInfoMap.get(slide.path)?.scene_id || slide.planData?.scene_id || slide.id || ''
                      )}
                      ExpandableSlideCard={ExpandableSlideCard}
                    />
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            /* Table View */
            <SlideTableView
              slides={slides}
              slideInfoMap={slideInfoMap}
              extractSlideNumber={extractSlideNumber}
              extractTitle={extractTitle}
              onSlideClick={onSlideClick}
              formatDuration={formatDuration}
            />
          )}

          {/* Empty State */}
          {slides.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                No scenes found
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Start a conversation to generate scenes, or check if this
                conversation has any content.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Export Dialog removed - feature is waitlisted */}
    </div>
  );
};

// Style Layout Card Component - Shows reference image and generated layout side by side
const StyleLayoutCard: React.FC<{
  slide: Slide;
  layoutState?: LayoutState;
  extractTitle: (content: string) => string;
  onSlideClick?: (slidePath: string) => void;
  conversationId?: string;
  index?: number;
}> = ({
  slide,
  layoutState,
  extractTitle,
  onSlideClick,
  conversationId,
  index = 0,
}) => {
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.12);
  const [shouldLoadIframe, setShouldLoadIframe] = useState(false);

  // Lazy load iframe based on visibility
  useEffect(() => {
    // Load immediately if in first 3 slides
    if (index < 3) {
      setShouldLoadIframe(true);
      return;
    }

    // Set up intersection observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadIframe) {
            console.log(`SlideGallery - Lazy loading style iframe for slide ${index}`);
            setShouldLoadIframe(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index, shouldLoadIframe]);

  // Load reference image
  useEffect(() => {
    const loadReferenceImage = async () => {
      if (!layoutState?.referenceImagePath || !conversationId) {
        setImageLoading(false);
        return;
      }

      try {
        // Construct the image URL with smart cache busting
        const cleanPath = layoutState.referenceImagePath.startsWith('./') 
          ? layoutState.referenceImagePath.slice(2) 
          : layoutState.referenceImagePath;
        const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/');
        // API_BASE_URL is now dynamically retrieved via getApiBaseUrl()
        const baseUrl = getApiBaseUrl();
        const baseImageUrl = `${baseUrl}/filesystem/image/${encodedPath}?conversationId=${conversationId}`;
        
        // Use a stable cache key based on conversation and path
        const cacheKey = `${conversationId}_${cleanPath.replace(/[^a-zA-Z0-9]/g, '_')}`;
        const imageUrl = `${baseImageUrl}&v=${cacheKey}`;
        console.log(`SlideGallery - Loading reference image with stable cache key: ${imageUrl}`);
        setReferenceImageUrl(imageUrl);
        setImageLoading(false);
      } catch (error) {
        console.error("Failed to load reference image:", error);
        setImageError(true);
        setImageLoading(false);
      }
    };

    loadReferenceImage();
  }, [layoutState, conversationId]);

  // Scale calculation for iframe
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const scaleX = containerWidth / DEFAULT_SLIDE_RESOLUTION.width;
        const scaleY = containerHeight / DEFAULT_SLIDE_RESOLUTION.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener("resize", updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  // Trigger animations in iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && shouldLoadIframe) {
      const handleLoad = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.postMessage({ type: "startAnimations" }, "*");
          } catch (error) {
            console.debug("Could not send animation trigger to iframe:", error);
          }
        }, 150);
      };

      iframe.addEventListener("load", handleLoad);
      return () => iframe.removeEventListener("load", handleLoad);
    }
  }, [slide.content, shouldLoadIframe]);

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSlideClick) {
      onSlideClick(slide.path);
    }
  };

  return (
    <Card ref={cardRef} className="group cursor-pointer transition-all duration-300 hover:shadow-xl bg-card border border-border/60 hover:border-primary/50 relative overflow-hidden rounded-xl backdrop-blur-sm">
      {/* Professional hover overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10" />

      {/* Header with title and layout info */}
      <div className="p-4 border-b border-border/30 bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm text-foreground">
              {layoutState?.title || extractTitle(slide.content)}
            </h3>
            {layoutState?.tags && layoutState.tags.length > 1 && (
              <div className="flex items-center gap-2 mt-1">
                {layoutState.tags.slice(1, 3).map((tag, index) => (
                  <span key={index} className="text-xs text-muted-foreground capitalize bg-muted/50 px-2 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs font-medium hover:bg-primary/5 hover:border-primary/30 transition-colors"
            onClick={handleViewClick}
          >
            <svg
              className="w-3.5 h-3.5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            View
          </Button>
        </div>
      </div>

      {/* Side-by-side content */}
      <div className="grid grid-cols-2 gap-4 p-4">
        {/* Reference Image Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary rounded-full"></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Reference
            </span>
          </div>
          <div className="aspect-video bg-gradient-to-br from-muted/20 to-muted/5 overflow-hidden relative rounded-lg border border-border/30">
            {imageLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : imageError || !referenceImageUrl ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs">No reference</span>
              </div>
            ) : (
              <img
                src={referenceImageUrl}
                alt="Reference"
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error(`SlideGallery - Failed to load image: ${referenceImageUrl}`, e);
                  setImageError(true);
                }}
                onLoad={() => {
                  console.log(`SlideGallery - Successfully loaded image: ${referenceImageUrl}`);
                }}
              />
            )}
          </div>
        </div>

        {/* Generated Layout Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Generated
            </span>
          </div>
          <div
            ref={containerRef}
            className="aspect-video bg-gradient-to-br from-muted/20 to-muted/5 overflow-hidden relative rounded-lg border border-border/30"
          >
            {shouldLoadIframe ? (
              <iframe
                ref={iframeRef}
                srcDoc={slide.content}
                key={`style-iframe-${slide.id}`} // Use slide.id which changes when content updates
                className="absolute border-0 pointer-events-none"
                title={extractTitle(slide.content)}
                sandbox="allow-same-origin allow-scripts"
                style={{
                  width: `${DEFAULT_SLIDE_RESOLUTION.width}px`,
                  height: `${DEFAULT_SLIDE_RESOLUTION.height}px`,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  left: "0",
                  top: "0",
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/10">
                <span className="text-xs text-muted-foreground">Loading...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description if available */}
      {layoutState?.description && (
        <div className="px-4 pb-4">
          <p className="text-xs text-muted-foreground line-clamp-2">
            {layoutState.description}
          </p>
        </div>
      )}
    </Card>
  );
};

// Generated Slide Plan View Component - Shows plan details for generated slides
const GeneratedSlidePlanView: React.FC<{
  slideInfo?: SlideInfo;
  slide: Slide;
  extractSlideNumber: (path: string) => string;
  formatDuration: (seconds: number) => string;
}> = ({ slideInfo, slide, extractSlideNumber, formatDuration }) => {
  const [activeTab, setActiveTab] = React.useState<'overview' | 'script'>('overview');
  
  // Try to get plan data from slideInfo or slide
  const planData = slideInfo || slide.planData;
  
  if (!planData) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">No plan details available for this slide.</p>
      </div>
    );
  }

  const duration = planData.estimated_duration || 0;

  return (
    <div className="h-full flex flex-col p-4 relative">
      {/* Unified Header - matches preview style */}
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-primary">
            Scene {extractSlideNumber(slide.path)}:
          </span>
          <h3 className="font-semibold text-sm text-foreground flex-1 truncate">
            {planData.title || slide.title}
          </h3>
        </div>
      </div>

      {/* Simplified Tabbed Content */}
      <div className="flex-1 flex flex-col bg-white/50 backdrop-blur-sm rounded-lg border border-border/20 min-h-0">
        {/* Tab Headers - Only Overview and Script */}
        <div className="flex border-b border-border/20">
          <button
            onClick={() => setActiveTab('overview')}
            className={cn(
              "flex-1 px-4 py-2 text-xs font-medium transition-all",
              activeTab === 'overview'
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('script')}
            className={cn(
              "flex-1 px-4 py-2 text-xs font-medium transition-all",
              activeTab === 'script'
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            Script
          </button>
        </div>

        {/* Tab Content - Cleaner and simpler */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0 scrollbar-thin">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Content Description - Main focus */}
              {planData.content_description && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground mb-2">
                    What This Scene Shows
                  </h4>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {planData.content_description}
                  </p>
                </div>
              )}

              {/* Visual Instructions - if available */}
              {'visual_instructions' in planData && planData.visual_instructions && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground mb-2">
                    Visual Direction
                  </h4>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {planData.visual_instructions}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'script' && (
            <div className="space-y-4">
              {/* Script Text - Clean and readable */}
              {planData.script_text ? (
                <div>
                  <h4 className="text-xs font-semibold text-foreground mb-2">
                    Voice-Over Script
                  </h4>
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                    {planData.script_text}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No script available for this scene.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Layout badge - bottom left */}
      {planData.layout_type && (
        <div className="absolute bottom-4 left-4 z-10">
          <span className="bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize">
            {planData.layout_type.replace("layout_", "").replace(/_/g, " ")}
          </span>
        </div>
      )}

      {/* Duration badge - bottom right */}
      {duration > 0 && (
        <div className="absolute bottom-4 right-4 z-10">
          <div className="bg-black/80 backdrop-blur-md text-white text-xs font-medium px-2 py-1 rounded-md flex items-center gap-1">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formatDuration(duration)}
          </div>
        </div>
      )}
    </div>
  );
};

// Expandable Slide Card Component with Preview/Plan Toggle
const ExpandableSlideCard: React.FC<{
  slide: Slide;
  slideInfo?: SlideInfo;
  extractSlideNumber: (path: string) => string;
  extractTitle: (content: string) => string;
  onSlideClick?: (slidePath: string) => void;
  formatDuration: (seconds: number) => string;
  index?: number;
}> = ({
  slide,
  slideInfo,
  extractSlideNumber,
  extractTitle,
  onSlideClick,
  formatDuration,
  index = 0,
}) => {
  const [viewMode, setViewMode] = useState<'preview' | 'plan'>('preview');
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.15);
  const [shouldLoadIframe, setShouldLoadIframe] = useState(false);

  // Debug: Monitor slide content changes
  useEffect(() => {
    console.log(`ExpandableSlideCard - Slide ${extractSlideNumber(slide.path)} content changed`);
    console.log(`  - Content length: ${slide.content?.length}`);
    console.log(`  - First 100 chars: ${slide.content?.substring(0, 100)}`);
    console.log(`  - justUpdated flag: ${slide.justUpdated}`);
  }, [slide.content]);

  // Lazy load iframe based on visibility
  useEffect(() => {
    // Load immediately if in first 3 slides
    if (index < 3) {
      setShouldLoadIframe(true);
      return;
    }

    // Set up intersection observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadIframe) {
            console.log(`SlideGallery - Lazy loading iframe for slide ${index}`);
            setShouldLoadIframe(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading slightly before visible
        threshold: 0.01
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index, shouldLoadIframe]);

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const scaleX = containerWidth / DEFAULT_SLIDE_RESOLUTION.width;
        const scaleY = containerHeight / DEFAULT_SLIDE_RESOLUTION.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener("resize", updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && shouldLoadIframe) {
      const handleLoad = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.postMessage({ type: "startAnimations" }, "*");
          } catch (error) {
            console.debug("Could not send animation trigger to iframe:", error);
          }
        }, 150);
      };

      iframe.addEventListener("load", handleLoad);
      return () => iframe.removeEventListener("load", handleLoad);
    }
  }, [slide.content, shouldLoadIframe]);

  // Trigger animations when switching to preview mode
  useEffect(() => {
    if (viewMode === 'preview' && iframeRef.current && shouldLoadIframe) {
      // Small delay to ensure iframe is ready
      setTimeout(() => {
        try {
          if (iframeRef.current) {
            iframeRef.current.contentWindow?.postMessage({ type: "startAnimations" }, "*");
          }
        } catch (error) {
          console.debug("Could not restart animations:", error);
        }
      }, 150);
    }
  }, [viewMode, shouldLoadIframe]);

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSlideClick) {
      onSlideClick(slide.path);
    }
  };

  const duration =
    slideInfo?.start_time !== undefined && slideInfo?.end_time !== undefined
      ? slideInfo.end_time - slideInfo.start_time
      : slideInfo?.estimated_duration || 0;

  return (
    <Card
      ref={cardRef}
      className="group transition-all duration-300 hover:shadow-xl bg-card border border-border/60 hover:border-primary/50 relative overflow-hidden h-[380px] flex flex-col rounded-xl backdrop-blur-sm"
    >
      {/* Toggle Bar */}
      <div className="flex-shrink-0 border-b bg-muted/30 overflow-hidden">
        <div className="flex">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewMode('preview');
            }}
            className={cn(
              "flex-1 py-2 px-3 text-xs font-medium transition-all flex items-center justify-center gap-1",
              viewMode === 'preview' 
                ? "bg-background shadow-sm text-foreground" 
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>Preview</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewMode('plan');
            }}
            className={cn(
              "flex-1 py-2 px-3 text-xs font-medium transition-all flex items-center justify-center gap-1",
              viewMode === 'plan' 
                ? "bg-background shadow-sm text-foreground" 
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Plan</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative">
        {viewMode === 'preview' ? (
          /* Preview Content */
          <div className="h-full flex flex-col p-4 relative">
            {/* Unified Header - same as plan view */}
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary">
                  Scene {extractSlideNumber(slide.path)}:
                </span>
                <h3 className="font-semibold text-sm text-foreground flex-1 truncate">
                  {slideInfo?.title || extractTitle(slide.content)}
                </h3>
              </div>
            </div>

            {/* Preview Section - Clickable for full screen */}
            <div 
              className="relative flex-1 cursor-pointer rounded-lg overflow-hidden bg-gradient-to-br from-muted/20 to-muted/5" 
              onClick={handlePreviewClick}
              title="Click to view full screen"
            >
              <div
                ref={containerRef}
                className="h-full overflow-hidden relative flex items-center justify-center"
              >
                {shouldLoadIframe ? (
                  <iframe
                    key={`iframe-${slide.id}`} // Use slide.id which now changes when content updates
                    ref={iframeRef}
                    srcDoc={slide.content}
                    className="absolute border-0 pointer-events-none"
                    title={extractTitle(slide.content)}
                    sandbox="allow-same-origin allow-scripts"
                    style={{
                      width: `${DEFAULT_SLIDE_RESOLUTION.width}px`,
                      height: `${DEFAULT_SLIDE_RESOLUTION.height}px`,
                      transform: `scale(${scale})`,
                      transformOrigin: "center",
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/10">
                    <span className="text-xs text-muted-foreground">Loading...</span>
                  </div>
                )}

                {/* Hover overlay with expand icon */}
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-all duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="bg-white/95 backdrop-blur-md rounded-full p-3 shadow-lg">
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Layout badge - bottom left */}
            {slideInfo?.layout_type && (
              <div className="absolute bottom-4 left-4 z-30 pointer-events-none">
                <span className="bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize">
                  {slideInfo.layout_type.replace("layout_", "").replace(/_/g, " ")}
                </span>
              </div>
            )}

            {/* Duration badge - bottom right */}
            {duration > 0 && (
              <div className="absolute bottom-4 right-4 z-30 pointer-events-none">
                <div className="bg-black/80 backdrop-blur-md text-white text-xs font-medium px-2 py-1 rounded-md flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {formatDuration(duration)}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Plan View Content */
          <div className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            <GeneratedSlidePlanView 
              slideInfo={slideInfo}
              slide={slide}
              extractSlideNumber={extractSlideNumber}
              formatDuration={formatDuration}
            />
          </div>
        )}
      </div>

    </Card>
  );
};

// Table View Component
const SlideTableView: React.FC<{
  slides: Slide[];
  slideInfoMap: Map<string, SlideInfo>;
  extractSlideNumber: (path: string) => string;
  extractTitle: (content: string) => string;
  onSlideClick?: (slidePath: string) => void;
  formatDuration: (seconds: number) => string;
}> = ({
  slides,
  slideInfoMap,
  extractSlideNumber,
  extractTitle,
  onSlideClick,
  formatDuration,
}) => {
  const handleRowClick = (slide: Slide) => {
    if (onSlideClick) {
      onSlideClick(slide.path);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border/50 shadow-sm overflow-hidden">
      {/* Table Header */}
      <div className="bg-muted/30 border-b border-border/50">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 text-sm font-medium text-muted-foreground">
          <div className="col-span-1">#</div>
          <div className="col-span-4">Title</div>
          <div className="col-span-2">Layout</div>
          <div className="col-span-2">Duration</div>
          <div className="col-span-2">Timeline</div>
          <div className="col-span-1">Actions</div>
        </div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-border/30">
        {slides.map((slide) => {
          const slideInfo = slideInfoMap.get(slide.path);
          const duration =
            slideInfo?.start_time !== undefined &&
            slideInfo?.end_time !== undefined
              ? slideInfo.end_time - slideInfo.start_time
              : slideInfo?.estimated_duration || slide.planData?.estimated_duration || 0;

          return (
            <div
              key={slide.id}
              className={cn(
                `grid grid-cols-12 gap-4 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group`,
                slide.source === 'html' ? 'cursor-pointer' : 'cursor-default',
                slide.source === 'plan' && 'bg-orange-50/30',
                slide.justUpdated && 'slide-updated'
              )}
              onClick={() => slide.source === 'html' ? handleRowClick(slide) : undefined}
            >
              {/* Slide Number */}
              <div className="col-span-1 flex items-center">
                <div className="w-8 h-8 bg-primary/10 rounded-md flex items-center justify-center text-sm font-semibold text-primary">
                  {slide.source === 'plan' ? extractSlideNumber(slide.planData?.scene_id || '') : extractSlideNumber(slide.path)}
                </div>
              </div>

              {/* Title */}
              <div className="col-span-4 flex items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-foreground truncate">
                      {slide.source === 'plan' ? slide.title : (slideInfo?.title || extractTitle(slide.content))}
                    </h3>
                    {slide.source === 'plan' && (
                      <span className="bg-orange-100 text-orange-800 text-xs font-medium px-1.5 py-0.5 rounded-full border border-orange-200 flex-shrink-0">
                        Not Generated
                      </span>
                    )}
                  </div>
                  {(slideInfo?.script_text || slide.planData?.script_text) && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">
                      {(slideInfo?.script_text || slide.planData?.script_text || '').slice(0, 80)}...
                    </p>
                  )}
                </div>
              </div>

              {/* Layout Type */}
              <div className="col-span-2 flex items-center">
                {(slideInfo?.layout_type || slide.planData?.layout_type) ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent text-accent-foreground capitalize">
                    {(slideInfo?.layout_type || slide.planData?.layout_type || '')
                      .replace("layout_", "")
                      .replace("_", " ")}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </div>

              {/* Duration */}
              <div className="col-span-2 flex items-center">
                {duration > 0 ? (
                  <div className="flex items-center gap-1.5 text-sm">
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="font-medium">
                      {formatDuration(duration)}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </div>

              {/* Timeline */}
              <div className="col-span-2 flex items-center">
                {slideInfo?.start_time !== undefined &&
                slideInfo?.end_time !== undefined ? (
                  <div className="text-xs font-mono text-muted-foreground">
                    <div>
                      {Math.floor(slideInfo.start_time / 60)}:
                      {String(Math.floor(slideInfo.start_time % 60)).padStart(
                        2,
                        "0",
                      )}
                    </div>
                    <div>
                      {Math.floor(slideInfo.end_time / 60)}:
                      {String(Math.floor(slideInfo.end_time % 60)).padStart(
                        2,
                        "0",
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </div>

              {/* Actions */}
              <div className="col-span-1 flex items-center">
                {slide.source === 'html' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(slide);
                    }}
                    title="View Scene"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">Plan Only</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State for Table */}
      {slides.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 bg-muted/20 rounded-full flex items-center justify-center mb-3">
            <svg
              className="w-6 h-6 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-foreground mb-1">
            No scenes found
          </h3>
          <p className="text-xs text-muted-foreground max-w-sm">
            Start a conversation to generate scenes, or check if this
            conversation has any content.
          </p>
        </div>
      )}
    </div>
  );
};

// Export memoized component to prevent re-renders when parent updates
export const SlideGallery = memo(SlideGalleryComponent, (prevProps, nextProps) => {
  // Only re-render if conversationId actually changes
  return prevProps.conversationId === nextProps.conversationId;
});

// Plan-Only Slide Card Component - Shows plan information when no HTML slide exists
const PlanOnlySlideCard: React.FC<{
  slide: Slide;
  extractSlideNumber: (path: string) => string;
  formatDuration: (seconds: number) => string;
  onCancelGeneration?: (slideId: string) => void;
  onClearError?: (slideId: string) => void;
  generationStatus?: SlideGenerationStatus;
}> = ({
  slide,
  extractSlideNumber,
  formatDuration,
  onCancelGeneration,
  onClearError,
  generationStatus,
}) => {
  const [activeTab, setActiveTab] = React.useState<'overview' | 'script'>('overview');
  const [linearProgress, setLinearProgress] = React.useState(0);

  if (!slide.planData) {
    return null;
  }

  const { planData } = slide;
  const duration = planData.estimated_duration || 0;

  // Get active job for thinking display
  const { job } = useAgentJob(generationStatus?.jobId);

  const isGenerating = generationStatus?.status === 'generating';
  const hasError = generationStatus?.status === 'error';

  // Linear progress timer - simulate 2 minute generation
  React.useEffect(() => {
    if (!isGenerating) {
      setLinearProgress(0);
      return;
    }

    // Start linear timer - 2 minutes = 120 seconds
    const startTime = Date.now();
    const duration = 120000; // 2 minutes in milliseconds

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercent = Math.min((elapsed / duration) * 100, 99); // Cap at 99%
      setLinearProgress(progressPercent);
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [isGenerating]);

  // When job completes, jump to 100%
  React.useEffect(() => {
    if (generationStatus?.status === 'completed') {
      setLinearProgress(100);
    }
  }, [generationStatus?.status]);

  return (
    <Card
      className="group cursor-default transition-all duration-300 hover:shadow-xl bg-card border border-border/60 hover:border-orange-300 relative overflow-hidden h-[380px] rounded-xl backdrop-blur-sm"
    >
      {/* "Not Generated" overlay - subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-transparent to-orange-100/50 opacity-100 transition-opacity duration-300 pointer-events-none z-10" />

      {/* Professional Info Section */}
      <div className="p-4 relative z-20 h-full flex flex-col">
        {/* Unified Header - Clean header with just scene info */}
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-primary">
              Scene {extractSlideNumber(planData.scene_id || "")}:
            </span>
            <h3 className="font-semibold text-sm text-foreground flex-1 truncate">
              {slide.title || planData.title}
            </h3>

            {/* Generating status badge */}
            {isGenerating && (
              <span className="bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-primary/30 flex items-center gap-1">
                <svg className="h-3 w-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Generating
              </span>
            )}
          </div>
        </div>

        {/* Generating State - Progress and Thinking */}
        {isGenerating && (
          <div className="mb-3 space-y-2">
            {/* Progress Bar - Linear 2-minute timer */}
            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-300 ease-out"
                style={{ width: `${linearProgress}%` }}
              />
            </div>

            {/* Agent Thinking Display */}
            {job?.progress?.thinking && (
              <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 bg-accent/50 p-2 rounded-md border border-primary/30">
                <svg className="h-3 w-3 animate-spin flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="flex-1">
                  <span className="font-medium text-accent-foreground">Slide Composer: </span>
                  {job.progress.thinking}
                </span>
              </div>
            )}

            {/* Cancel Generation Button */}
            {onCancelGeneration && planData.scene_id && (
              <div className="flex justify-end pt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelGeneration(planData.scene_id);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200 hover:border-red-300 flex items-center gap-1.5"
                  title="Cancel generation and clear status"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel Generation
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {hasError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start gap-2">
              <svg className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="text-xs font-semibold text-red-800 mb-1">Generation Failed</h4>
                    <p className="text-xs text-red-700">{generationStatus?.error || 'Unknown error occurred'}</p>
                  </div>
                  {onClearError && planData.scene_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearError(planData.scene_id);
                      }}
                      className="px-2 py-1 text-xs font-medium text-red-700 hover:text-red-900 hover:bg-red-100 rounded-md transition-colors flex-shrink-0"
                      title="Clear this error message"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Simplified Tabbed Content - flex-1 to fill remaining space */}
        <div className="flex-1 flex flex-col bg-white/50 backdrop-blur-sm rounded-lg border border-border/20 min-h-0">
          {/* Tab Headers - Only Overview and Script */}
          <div className="flex border-b border-border/20 flex-shrink-0">
            <button
              onClick={() => setActiveTab('overview')}
              className={cn(
                "flex-1 px-4 py-2 text-xs font-medium transition-all",
                activeTab === 'overview'
                  ? "text-primary border-b-2 border-primary bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('script')}
              className={cn(
                "flex-1 px-4 py-2 text-xs font-medium transition-all",
                activeTab === 'script'
                  ? "text-primary border-b-2 border-primary bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              Script
            </button>
          </div>

          {/* Tab Content - scrollable with min-h-0 */}
          <div className="flex-1 overflow-y-auto p-4 scrollbar-thin min-h-0">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Content Description - Main focus */}
                <div>
                  <h4 className="text-xs font-semibold text-foreground mb-2">
                    What This Scene Shows
                  </h4>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {planData.content_description}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'script' && (
              <div className="space-y-4">
                {/* Script Text - Clean and readable */}
                {planData.script_text ? (
                  <div>
                    <h4 className="text-xs font-semibold text-foreground mb-2">
                      Voice-Over Script
                    </h4>
                    <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                      {planData.script_text}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No script available for this scene.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Layout badge - bottom left */}
        {planData.layout_type && (
          <div className="absolute bottom-4 left-4 z-20">
            <span className="bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize">
              {planData.layout_type.replace("layout_", "").replace(/_/g, " ")}
            </span>
          </div>
        )}

        {/* Bottom right - Duration badge */}
        {!isGenerating && duration > 0 && (
          <div className="absolute bottom-4 right-4 z-20 flex items-center gap-2">
            <div className="bg-black/80 backdrop-blur-md text-white text-xs font-medium px-2 py-1 rounded-md flex items-center gap-1">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatDuration(duration)}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
